import React, {createContext, useContext, useEffect, useState} from 'react';
import {decodeJWT} from "../../utils/jwtUtils";
import {useDispatch, useSelector} from "react-redux";
import {toast} from "react-toastify";
import {PublicClientApplication} from "@azure/msal-browser";
import {
    checkJWTRequest,
    loginRequest,
    logoutRequest,
    selectRoleRequest
} from "../../redux/actions/authenticationActions";
import {apiCall} from "../../redux/utils/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const dispatch = useDispatch();
    const authToken = useSelector(state => state.authentication.authToken);
    const userGroups = useSelector(state => state.authentication.userGroups);
    const selectedRole = useSelector(state => state.authentication.selectedRole);
    const isLoadingLogin = useSelector(state => state.authentication.loading.login);
    const isLoadingCheckJWT = useSelector(state => state.authentication.loading.checkJWT);
    const loadedCheckJWT = useSelector(state => state.authentication.loadedCheckJWT);
    const [msalInstance, setMsalInstance] = useState(null);

    useEffect(() => {
        const initializeMsal = async () => {
            const msalConfig = {
                auth: {
                    clientId: `${process.env.MS_CLIENT_ID}`,
                    authority: `https://login.microsoftonline.com/${process.env.MS_TENANT_ID}`, // Ersetzen Sie dies mit Ihrer Tenant-ID
                    redirectUri: `${process.env.REACT_DOMAIN}/auth` // Ersetzen Sie dies mit Ihrer Redirect-URI
                }
            };
            try {
                const msalInstance = await PublicClientApplication.createPublicClientApplication(msalConfig);
                setMsalInstance(msalInstance);
            } catch (error) {
                console.error("Fehler bei der Initialisierung von MSAL:", error);
            }
        };
        initializeMsal();
    }, []);

    useEffect(() => {
        if (authToken && authToken !== 'BFF_SESSION') {
            const decoded = decodeJWT(authToken);
            const currentTime = Date.now() / 1000;

            if (decoded && decoded.exp < currentTime) {
                dispatch(logoutRequest());
            }
        }

        const storedToken = localStorage.getItem('authToken');

        if (
            !authToken &&
            !isLoadingCheckJWT &&
            !loadedCheckJWT &&
            storedToken &&
            storedToken !== "undefined"
        ) {
            dispatch(checkJWTRequest({ checkJWT: 1 }));
        }
    }, [dispatch, authToken, isLoadingCheckJWT, loadedCheckJWT]);

    useEffect( () => {
        if (userGroups.length > 1 && selectedRole === "") {
            if (localStorage.getItem('selectedRole')) {
                dispatch(selectRoleRequest({selectedRole: localStorage.getItem('selectedRole')}));
            }
        }
    }, [userGroups, selectedRole, dispatch]);

    const handleLogin = async (credentials) => {
        if (!isLoadingLogin) {
            dispatch(loginRequest(credentials));
        }
    };

    const handleCheckJWT = async () => {
        if (localStorage.getItem("authToken") && localStorage.getItem("authToken") !== "undefined") {
            const token = localStorage.getItem("authToken");
            if (token) {
                const decoded = decodeJWT(token);
                const currentTime = Date.now() / 1000;
                if (decoded && decoded.exp > currentTime && !isLoadingCheckJWT && !loadedCheckJWT) {
                    dispatch(checkJWTRequest({checkJWT: 1}));
                }
            }
        }
    }

    const handleMicrosoftLogin = async (onSuccess) => {
        if (!msalInstance) {
            console.error("MSAL-Instanz ist noch nicht bereit.");
            return;
        }
        try {
            const authResult = await msalInstance.loginPopup({
                scopes: ["User.Read"]
            });

            if (authResult) {
                const mstoken = authResult.accessToken;
                authenticateUser(mstoken, `${process.env.REACT_BASE_API_URL}/login-api/loginMicrosoft`, onSuccess);
            }
        } catch (error) {
            console.error("Fehler bei der Microsoft-Authentifizierung:", error);
        }
    };

    const authenticateUser = async (mstoken, url, onSuccess) => {
        try {
            const data = await apiCall(url, 'POST', { mstoken });
            if (!data.error) {
                localStorage.setItem('authToken', data.token);
                const decoded = decodeJWT(data.token);
                if (onSuccess) {
                    onSuccess(decoded?.Groups || []);
                }
            } else {
                toast("Authentifizierungsfehler", { type: "danger" });
            }
        } catch (error) {
            console.error('Fehler bei der Authentifizierung:', error);
            toast("Authentifizierungsfehler", { type: "danger" });
        }
    };

    return (
        <AuthContext.Provider value={{ authToken, userGroups, handleLogin, handleCheckJWT }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
